/**
 * RESPONSIBILITY: draw one column of a row.
 *
 * Which cell draws which kind lives in list-cells.tsx; this is the label above it. The
 * name is drawn HERE, once, for every kind — left to the cells, only the ones wrapping
 * an input that takes a placeholder could show one, so an image column was an unnamed
 * thumbnail and a filled text column lost its name the moment it had a value.
 */
import { type CSSProperties } from 'react';
import { cellFor } from './list-cells';
import type { ItemField } from './cell-types';

export interface RowCellProps {
  field: ItemField;
  /** Unique within the form. */
  id: string;
  index: number;
  row: Record<string, unknown>;
  root: Record<string, unknown>;
  enumVals: string[] | undefined;
  onChange: (v: unknown) => void;
}

export function RowCell({ field, id, index, row, root, enumVals, onChange }: RowCellProps) {
  const cell = cellFor(field, enumVals);
  const Draw = cell.render;

  return (
    <div
      className="ctl-cell"
      style={{ '--cell-basis': cell.basis(field), '--cell-grow': cell.grow ?? 1 } as CSSProperties}
    >
      {/* htmlFor rather than wrapping: the image cell contains a <label> of its own for
          the file input, and labels cannot nest. */}
      <label className="ctl-cell-label" htmlFor={id}>{field.label}</label>
      <Draw
        field={field} enumVals={enumVals} index={index} id={id}
        root={root} row={row} value={row[field.key]} onChange={onChange}
      />
      {/* WRITTEN OUT, not hidden in a tooltip. A `title` is help nobody discovers: it
          needs the pointer held still over the right few pixels, it never appears on a
          phone, and the one column whose explanation actually matters — the theme that
          decides what the rest of the row asks for — was the one nobody could read. */}
      {field.description && <p className="ctl-cell-help">{field.description}</p>}
    </div>
  );
}
